import { useMemo } from "react";
import { useExperienceContext } from "./useExperienceContext";
import {
  resolveCanonicalExperienceContext,
  type ResolvedCanonicalExperienceContext,
} from "../resolveCanonicalExperienceContext";
import type { CanonicalExperienceContext } from "../canonicalExperienceContext";

export type AuthorizedExperienceProjection = {
  status: "loading" | "absent" | "ready" | "error";
  canonical: CanonicalExperienceContext | null;
  projection: ResolvedCanonicalExperienceContext | null;
  error: Error | null;
};

/**
 * The single consumer-facing read model for journey-defining state.
 * Consumers should use the canonical projection when it is available, while
 * keeping user-entered data and editable flows available when it is absent or
 * temporarily unavailable.
 */
export function useAuthorizedExperienceProjection(): AuthorizedExperienceProjection {
  const query = useExperienceContext();

  return useMemo(() => {
    if (query.isLoading) {
      return {
        status: "loading",
        canonical: null,
        projection: null,
        error: null,
      };
    }

    if (query.error) {
      return {
        status: "error",
        canonical: null,
        projection: null,
        error:
          query.error instanceof Error
            ? query.error
            : new Error(String(query.error)),
      };
    }

    if (!query.data) {
      return {
        status: "absent",
        canonical: null,
        projection: null,
        error: null,
      };
    }

    return {
      status: "ready",
      canonical: query.data,
      projection: resolveCanonicalExperienceContext(query.data),
      error: null,
    };
  }, [query.data, query.error, query.isLoading]);
}
